import React, {useState} from 'react';
import {Grid, Typography} from '@material-ui/core';
import {observer} from 'mobx-react-lite';
import {Quote} from '../../../Core/mobx/ParameterStore/Quote';
import Card from '@material-ui/core/Card';
import {NumberFormatCustom, SwitchCustom, TextBox} from '../../../Core/UI';
import {Prequal} from '../../../Core/mobx/ParameterStore/Prequal';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {SelectInputElement} from '../../PreQualification/Components/SelectInputElement';
import {FormType} from '../../PreQualification/types';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/QuoteXref';

type LSWIULTabProps = {
    valueChanged: (p: Parameter, value: any, formType?: FormType) => void;
    parameters: Quote;
    refParameters: Prequal;
    formType: FormType;
};

const LSWIULTab = observer(({valueChanged, parameters, refParameters, formType}: LSWIULTabProps) => {
    const [error, setError] = useState({
        deathBenefitChangeAgeLSW: '',
    });

    const required = (value?: string) => (
        <span>
            {value && value} <span style={{color: 'red'}}>*</span>
        </span>
    );

    const validateDeathBenefitChangeAgeLSW = () => {
        setError((prevState) => ({
            ...prevState,
            deathBenefitChangeAgeLSW:
                parameters.deathBenefitChangeYear.value < refParameters.age.value ? 'deathBenefitChangeYear > age' : '',
        }));
    };

    return (
        <>
            <Card
                style={{
                    padding: 20,
                    marginBottom: 20,
                }}
            >
                <div>
                    <Typography style={{fontWeight: 'bold', fontSize: 24}}>Basics</Typography>
                </div>
                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <SelectInputElement
                            param={{
                                label: 'Health Rating',
                                name: 'healthRatingLSWIUL',
                                options: fieldOptions.healthRatingLSWIUL.options,
                                optionValues: fieldOptions.healthRatingLSWIUL.optionValues,
                                ...parameters.healthRatingLSWIUL,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            formType={formType}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <SelectInputElement
                            param={{
                                label: 'Table Rating',
                                name: 'tableRatingLSWIUL2',
                                options: fieldOptions.tableRatingLSWIUL2.options,
                                optionValues: fieldOptions.tableRatingLSWIUL2.optionValues,
                                ...parameters.tableRatingLSWIUL2,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            formType={formType}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <SwitchCustom
                            param={{
                                name: 'flatExtra',
                                label: 'Flat Extra',
                                ...parameters.flatExtra,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            formType={formType}
                        />
                    </Grid>
                    {parameters.flatExtra.value && (
                        <Grid item xs={12}>
                            <TextBox
                                id={'flatExtraPerm'}
                                label={'Policy Lifetime Flat Extra (per $1000)'}
                                value={parameters.flatExtraPerm?.value}
                                onChange={(value) => valueChanged(parameters.flatExtraPerm, value, formType)}
                                InputProps={{
                                    inputComponent: NumberFormatCustom,
                                }}
                            />
                        </Grid>
                    )}
                    {parameters.flatExtra.value && (parameters.ameritas.value || parameters.northAmerican.value) && (
                        <>
                            <Grid item xs={6}>
                                <TextBox
                                    id={'flatExtraYears'}
                                    label={'Temporary Flat Extra Years'}
                                    value={parameters.flatExtraYears?.value}
                                    onChange={(value) => valueChanged(parameters.flatExtraYears, value, formType)}
                                    type="number"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextBox
                                    id={'flatExtraTemp'}
                                    label={'Temporary Flat Extra Amount (per $1000)'}
                                    value={parameters.flatExtraTemp?.value}
                                    onChange={(value) => valueChanged(parameters.flatExtraTemp, value, formType)}
                                    InputProps={{
                                        inputComponent: NumberFormatCustom,
                                    }}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </Card>
            <Card
                style={{
                    padding: 20,
                    marginBottom: 20,
                }}
            >
                <div>
                    <Typography style={{fontWeight: 'bold', fontSize: 24}}>Coverage and Premium</Typography>
                </div>
                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <TextBox
                            id={'specifiedFaceAmountLSW'}
                            label={'Specified Face Amount'}
                            value={parameters.specifiedFaceAmountLSW?.value}
                            onChange={(value) => valueChanged(parameters.specifiedFaceAmountLSW, value, formType)}
                            InputProps={{
                                inputComponent: NumberFormatCustom,
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <SelectInputElement
                            param={{
                                label: 'Premium Payment Frequency',
                                name: 'premiumFrequency',
                                options: fieldOptions.premiumFrequency.options,
                                optionValues: fieldOptions.premiumFrequency.optionValues,
                                ...parameters.premiumFrequency,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            formType={formType}
                        />
                    </Grid>
                </Grid>
            </Card>
            <Card
                style={{
                    padding: 20,
                    marginBottom: 20,
                }}
            >
                <div>
                    <Typography style={{fontWeight: 'bold', fontSize: 24}}>Coverage and Premium Details</Typography>
                </div>
                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <SelectInputElement
                            param={{
                                label: 'Compliance Test',
                                name: 'complianceTestLSWIUL',
                                options: fieldOptions.complianceTestLSWIUL.options,
                                optionValues: fieldOptions.complianceTestLSWIUL.optionValues,
                                ...parameters.complianceTestLSWIUL,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            formType={formType}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <SelectInputElement
                            param={{
                                label: 'MEC Avoidance',
                                name: 'mecAvoidanceLSWIUL',
                                options: fieldOptions.mecAvoidanceLSWIUL.options,
                                optionValues: fieldOptions.mecAvoidanceLSWIUL.optionValues,
                                ...parameters.mecAvoidanceLSWIUL,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            formType={formType}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <SelectInputElement
                            param={{
                                label: 'Death Benefit Option',
                                name: 'deathBenefitOptionLSWIUL',
                                options: fieldOptions.deathBenefitOptionLSWIUL.options,
                                optionValues: fieldOptions.deathBenefitOptionLSWIUL.optionValues,
                                ...parameters.deathBenefitOptionLSWIUL,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            formType={formType}
                        />
                    </Grid>
                    {refParameters.age.value < 66 && (
                        <Grid item xs={6}>
                            <TextBox
                                id={'deathBenefitChangeAgeLSW'}
                                label={'Death Benefit Change Age (0 for no change)'}
                                value={parameters.deathBenefitChangeAgeLSW?.value}
                                onChange={(value) => {
                                    valueChanged(parameters.deathBenefitChangeAgeLSW, value, formType);
                                    validateDeathBenefitChangeAgeLSW();
                                }}
                                type="number"
                                error={!!error.deathBenefitChangeAgeLSW}
                                helperText={error.deathBenefitChangeAgeLSW}
                            />
                        </Grid>
                    )}
                    <Grid item xs={10}>
                        <SelectInputElement
                            param={{
                                label: 'Solve Target',
                                name: 'solveTargetLSWIUL',
                                options: fieldOptions.solveTargetLSWIUL.options,
                                optionValues: fieldOptions.solveTargetLSWIUL.optionValues,
                                ...parameters.solveTargetLSWIUL,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            formType={formType}
                        />
                    </Grid>
                    <Grid item xs={5}>
                        <TextBox
                            id={'firstYearLumpSumLSWIUL'}
                            label={'First Year Lump Sum'}
                            value={parameters.firstYearLumpSumLSWIUL?.value}
                            onChange={(value) => valueChanged(parameters.firstYearLumpSumLSWIUL, value, formType)}
                            InputProps={{
                                inputComponent: NumberFormatCustom,
                            }}
                        />
                    </Grid>
                    <Grid item xs={5}>
                        <SwitchCustom
                            param={{
                                name: 'deductFirstModalPremiumFromLumpSum',
                                label: 'Deduct First Modal Premium From Lump Sum',
                                ...parameters.deductFirstModalPremiumFromLumpSum,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            formType={formType}
                        />
                    </Grid>
                    {[...Array(5)].map((_, i) => (
                        <React.Fragment key={`lswPremiumSolveType-${i}`}>
                            <Grid item xs={4}>
                                <SelectInputElement
                                    param={{
                                        label: 'Premium Solve Type',
                                        name: `lswPremiumSolveType${i + 1}`,
                                        options: fieldOptions.lswPremiumSolveType1.options,
                                        optionValues: fieldOptions.lswPremiumSolveType1.optionValues,
                                        ...(parameters[
                                            `lswPremiumSolveType${i + 1}` as keyof typeof parameters
                                        ] as Parameter),
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextBox
                                    id={`lswPremiumAmount${i + 1}`}
                                    label={'Premium Amount (Specify Amount Only'}
                                    value={
                                        (parameters[`lswPremiumAmount${i + 1}` as keyof typeof parameters] as Parameter)
                                            ?.value
                                    }
                                    onChange={(value) =>
                                        valueChanged(
                                            parameters[
                                                `lswPremiumAmount${i + 1}` as keyof typeof parameters
                                            ] as Parameter,
                                            value,
                                            formType,
                                        )
                                    }
                                    type="number"
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <TextBox
                                    id={`lswPremiumStart${i + 1}`}
                                    label={'Premium Start Year (Number)'}
                                    value={
                                        (parameters[`lswPremiumStart${i + 1}` as keyof typeof parameters] as Parameter)
                                            ?.value
                                    }
                                    onChange={(value) =>
                                        valueChanged(
                                            parameters[
                                                `lswPremiumStart${i + 1}` as keyof typeof parameters
                                            ] as Parameter,
                                            value,
                                            formType,
                                        )
                                    }
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <TextBox
                                    id={`lswPremiumEnd${i + 1}`}
                                    label={"Premium End Year (Number or 'M')"}
                                    value={
                                        (parameters[`lswPremiumEnd${i + 1}` as keyof typeof parameters] as Parameter)
                                            ?.value
                                    }
                                    onChange={(value) =>
                                        valueChanged(
                                            parameters[`lswPremiumEnd${i + 1}` as keyof typeof parameters] as Parameter,
                                            value,
                                            formType,
                                        )
                                    }
                                />
                            </Grid>
                        </React.Fragment>
                    ))}
                </Grid>
            </Card>
            <Card
                style={{
                    padding: 20,
                    marginBottom: 20,
                }}
            >
                <div>
                    <Typography style={{fontWeight: 'bold', fontSize: 24}}>Policy rider</Typography>
                </div>
                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <SwitchCustom
                            param={{
                                name: 'deathBenefitProtection',
                                label: 'Death Benefit Protection',
                                ...parameters.deathBenefitProtection,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            formType={formType}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <SwitchCustom
                            param={{
                                name: 'ABRTerminalIllness',
                                label: 'ABR Terminal Illness',
                                ...parameters.ABRTerminalIllness,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            formType={formType}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <SwitchCustom
                            param={{
                                name: 'ABRChronicIllness',
                                label: 'ABR Chronic Illness',
                                ...parameters.ABRChronicIllness,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            formType={formType}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <SwitchCustom
                            param={{
                                name: 'ABRCriticalIllness',
                                label: 'ABR Critical Illness',
                                ...parameters.ABRCriticalIllness,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            formType={formType}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <SwitchCustom
                            param={{
                                name: 'ABRCriticalInjury',
                                label: 'ABR Critical Injury',
                                ...parameters.ABRCriticalInjury,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            formType={formType}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <TextBox
                            id={'ABRSummaryAge'}
                            label={'ABR Summary Age'}
                            value={parameters.ABRSummaryAge?.value}
                            onChange={(value) => valueChanged(parameters.ABRSummaryAge, value, formType)}
                            type="number"
                        />
                    </Grid>
                </Grid>
            </Card>
            <Card
                style={{
                    padding: 20,
                    marginBottom: 20,
                }}
            >
                <div>
                    <Typography style={{fontWeight: 'bold', fontSize: 24}}>Include Report</Typography>
                </div>
                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <SwitchCustom
                            param={{
                                name: 'includeReportLsw',
                                label: 'Include Report',
                                ...parameters.includeReportLsw,
                            }}
                            required={required('')}
                            valueChanged={valueChanged}
                            formType={formType}
                        />
                    </Grid>
                </Grid>
            </Card>
            <Card
                style={{
                    padding: 20,
                    marginBottom: 20,
                }}
            >
                <div>
                    <Typography style={{fontWeight: 'bold', fontSize: 24}}>Index Strategy</Typography>
                </div>
                <Grid container spacing={3}>
                    <Grid item xs={4}>
                        <TextBox
                            id={'SP500AllocationPercentage'}
                            label={'S&P Point to Point Cap Focus Allocation %'}
                            value={parameters.SP500AllocationPercentage?.value}
                            onChange={(value) => valueChanged(parameters.SP500AllocationPercentage, value, formType)}
                            type="number"
                        />
                        <small>The sum of allocation percentages for all strategies must equal 100%</small>
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id={'USPacesetterAllocationPercentage'}
                            label={'US Pacesetter Index Point to Point No cap Allocation %"'}
                            value={parameters.USPacesetterAllocationPercentage?.value}
                            onChange={(value) =>
                                valueChanged(parameters.USPacesetterAllocationPercentage, value, undefined)
                            }
                            type="number"
                        />
                    </Grid>
                </Grid>
            </Card>
        </>
    );
});

export default LSWIULTab;
